import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { Build } from '@/utils/api-pb';
import type { Viewport } from '@/components/builder/PreviewFrame';

const VIEWPORTS: Viewport[] = ['desktop', 'tablet', 'mobile'];

interface EditorToolbarProps {
  build: Build;
  version: number | null;
  viewport: Viewport;
  publishingEnabled: boolean;
  isBusy: boolean;
  hasContent: boolean;
  onViewport: (viewport: Viewport) => void;
  onBack: () => void;
  onOpenPanel: (panel: 'versions' | 'settings' | 'leads') => void;
  onRegenerate: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
}

/**
 * Everything above the preview.
 *
 * The publish control carries most of the meaning here: it has to say which of four
 * states the site is in — never published, live, taken down, suspended — and, when the
 * button is disabled, why. A greyed-out button with no explanation is the thing people
 * write support tickets about.
 */
export function EditorToolbar({
  build,
  version,
  viewport,
  publishingEnabled,
  isBusy,
  hasContent,
  onViewport,
  onBack,
  onOpenPanel,
  onRegenerate,
  onPublish,
  onUnpublish,
}: EditorToolbarProps) {
  const { t } = useTranslation('builder');

  const isPublished = build.status === 'published';
  const isSuspended = build.status === 'suspended';

  const blockedReason = !publishingEnabled
    ? t('editor.publishSoon')
    : isSuspended
      ? t('editor.publishSuspended')
      : !hasContent
        ? t('editor.publishNoContent')
        : !build.slug
          ? t('editor.publishNoSlug')
          : '';

  return (
    <div className="flex flex-wrap items-center gap-[12px]">
      <Button
        variant="brand-outline"
        size="brand-sm"
        onClick={onBack}
      >
        {t('editor.back')}
      </Button>

      <h1 className="font-manrope text-[18px] font-bold text-[color:var(--black-text,#0d0d0d)]">
        {build.name}
      </h1>

      {version !== null && (
        <span className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {t('editor.version', { n: version })}
        </span>
      )}

      {isPublished && build.url && (
        <a
          href={build.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-segoe text-[13px] text-[color:var(--main-brand,#2197f1)] hover:underline"
        >
          {build.url.replace(/^https:\/\//, '')}
        </a>
      )}

      <div className="ml-auto flex flex-wrap items-center gap-[8px]">
        <div className="flex rounded-[10px] bg-[var(--grey-background,#f8f8f8)] p-[2px]">
          {VIEWPORTS.map((option) => (
            <Button
              key={option}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onViewport(option)}
              aria-pressed={viewport === option}
              className={
                'h-auto rounded-[16px] px-[10px] py-[5px] font-manrope text-[13px] font-semibold transition-colors ' +
                (viewport === option
                  ? 'bg-white text-[color:var(--main-brand,#2197f1)] shadow-sm'
                  : 'text-[color:var(--grey-text,#565858)]')
              }
            >
              {t(`editor.viewport.${option}`)}
            </Button>
          ))}
        </div>

        <Button variant="brand-soft" size="brand-sm" onClick={() => onOpenPanel('leads')}>
          {build.leads > 0 ? t('editor.leadsWithCount', { count: build.leads }) : t('editor.leads')}
        </Button>
        <Button variant="brand-soft" size="brand-sm" onClick={() => onOpenPanel('versions')}>
          {t('editor.versions')}
        </Button>
        <Button variant="brand-soft" size="brand-sm" onClick={() => onOpenPanel('settings')}>
          {t('editor.settings')}
        </Button>
        <Button variant="brand-soft" size="brand-sm" onClick={onRegenerate} disabled={isBusy}>
          {t('editor.regenerate')}
        </Button>

        {isPublished && (
          <Button variant="brand-outline" size="brand-sm" onClick={onUnpublish} disabled={isBusy}>
            {t('editor.unpublish')}
          </Button>
        )}

        <Button
          variant="brand"
          size="brand"
          onClick={onPublish}
          disabled={isBusy || Boolean(blockedReason)}
          title={blockedReason || undefined}
        >
          {isPublished ? t('editor.republish') : t('editor.publish')}
        </Button>
      </div>

      {blockedReason && (
        <p className="basis-full font-segoe text-[13px] text-[color:var(--grey-text,#969696)]">
          {blockedReason}
        </p>
      )}
    </div>
  );
}
